/**
 * Dry-run hook for the WorkflowEditor.
 *
 * Calls workflow.dryRunDraft with the CURRENT draft graph (not a saved workflow id),
 * so the preview always reflects on-screen content — even before the first save.
 *
 * FIX 1 (P10-1): uses dryRunDraft instead of dryRun, so the backend runs the
 * on-screen draft rather than the last persisted graph. This also removes the
 * "disabled until saved" requirement on the Dry run button.
 *
 * FIX 2 (P10-2): when the dry-run result has slack.validationErrors, the hook
 * treats it as an ERROR state (sets `error`, does NOT clear stale, does NOT
 * populate `result.slack.renderedMessage`). An unknown token in the template now
 * surfaces as a visible error rather than a rendered "success" preview.
 *
 * On success (no validationErrors):
 *   - stores the DryRunResult (matches TestRunResult shape 1:1)
 *   - clears stale on all covered steps
 *   - derives discovered columns from result.query.sampleColumns
 *
 * On error (BAD_REQUEST from the server or validationErrors from the result):
 *   - stores the error message in `error` for the step to display inline
 *   - does NOT clear prior fresh results (keep stale affordance)
 */
import { useMutation } from '@tanstack/react-query';

import { useOrpc } from '@/_shared/hooks/useOrpc';

import { draftToGraph, scheduleToTrigger } from '../mappers';
import type { StepKey, TestRunResult, WorkflowDraft } from '../types';

/** Scalar token chips that are always available regardless of columns. */
export const SPECIAL_TOKENS = [
  { token: '{{row_count}}', description: 'rows returned' },
  { token: '{{range_start}}', description: 'trigger window start' },
  { token: '{{range_end}}', description: 'trigger window end' },
  { token: '{{day}}', description: 'trigger day (yyyy-mm-dd)' },
];

/**
 * The per-row loop block.
 *
 * FIX 3 (P10-3): the static fallback uses clearly-placeholder names that the user must
 * edit. When columns are known (from a prior dry-run), the editor builds a column-aware
 * snippet via `buildEachRowsBlock(columns)` instead of emitting this constant.
 */
export const EACH_ROWS_BLOCK_PLACEHOLDER = '{{#each rows}}• {{col1}} — {{col2}}\n{{/each}}';

/**
 * Build a loop snippet using the first discovered column.
 * Falls back to the placeholder when no columns are known yet.
 */
export function buildEachRowsBlock(columns: string[]): string {
  const first = columns[0];
  if (!first) return EACH_ROWS_BLOCK_PLACEHOLDER;
  return `{{#each rows}}• {{${first}}}\n{{/each}}`;
}

/** Per-node error map — each step shows only its own error inline (Issue 3). */
export type StepErrorMap = Partial<Record<StepKey, string>>;

export interface UseDryRunReturn {
  run: (draft: WorkflowDraft) => void;
  result: TestRunResult | null;
  /** Dry-run is in flight. */
  isRunning: boolean;
  /**
   * Top-level banner summary (a count when input validation flagged specific nodes, or the single
   * server message otherwise). NOT the per-node text — that lives in {@link stepErrors}.
   */
  error: string | null;
  /**
   * Per-node errors, so each step renders ONLY its own reason (a valid node shows nothing). Built from
   * the oRPC input-validation `data.issues[]` (path `["graph","nodes",<i>,"config",<field>]`) or from a
   * single handler error scoped to the node it concerns (Issue 3).
   */
  stepErrors: StepErrorMap;
  /** Columns discovered from the latest successful dry-run query result. */
  columns: string[];
  /** Clear the result (e.g. after a significant draft change if desired). */
  clear: () => void;
}

interface DryRunOptions {
  onStaleCleared: (cleared: ReadonlySet<StepKey>) => void;
}

/** Friendly labels for the node-config fields the graph schema validates (all presence checks). */
const FIELD_LABELS: Record<string, string> = {
  template: 'Message',
  channel: 'Channel',
  sql: 'Query',
  warehouseId: 'Warehouse',
  lhs: 'Condition',
  op: 'Condition',
  rhs: 'Condition',
};

/** A Standard-Schema issue (the shape oRPC puts in `data.issues` on a BAD_REQUEST input-validation error). */
interface StandardIssue {
  message: string;
  path?: readonly (PropertyKey | { key: PropertyKey })[];
}

/** Standard-Schema path segments may be a raw key or `{ key }` — normalize to the raw key. */
function pathKey(seg: PropertyKey | { key: PropertyKey }): PropertyKey {
  return typeof seg === 'object' && seg !== null && 'key' in seg ? seg.key : seg;
}

/** Pull `data.issues[]` off an oRPC BAD_REQUEST input-validation error; null if it isn't that shape. */
function extractIssues(err: unknown): StandardIssue[] | null {
  if (!err || typeof err !== 'object') return null;
  const data = (err as { data?: unknown }).data;
  if (!data || typeof data !== 'object') return null;
  const issues = (data as { issues?: unknown }).issues;
  return Array.isArray(issues) && issues.length > 0 ? (issues as StandardIssue[]) : null;
}

/**
 * Map oRPC input-validation issues to per-node errors. Each issue's path is
 * `["graph","nodes",<i>,"config",<field>]`; we resolve `<i>` to the node kind via the SAME graph we
 * sent, so the message lands on the right step regardless of node ordering. All current node-config
 * validations are presence checks, so a known field becomes "<Label> is required"; anything else falls
 * back to the raw issue message.
 */
function issuesToStepErrors(issues: StandardIssue[], nodes: readonly { kind: string }[]): StepErrorMap {
  const map: StepErrorMap = {};
  for (const issue of issues) {
    const segs = (issue.path ?? []).map(pathKey);
    if (segs[0] !== 'graph' || segs[1] !== 'nodes') continue;
    const idx = Number(segs[2]);
    const kind = Number.isInteger(idx) ? nodes[idx]?.kind : undefined;
    if (kind !== 'query' && kind !== 'condition' && kind !== 'slack') continue;
    const field = typeof segs[4] === 'string' ? segs[4] : undefined;
    const msg = field && FIELD_LABELS[field] ? `${FIELD_LABELS[field]} is required` : issue.message;
    map[kind] = map[kind] ? `${map[kind]}; ${msg}` : msg;
  }
  return map;
}

/** Map a handler DryRunError `code` (no structured issues) to the step it concerns. */
function stepForErrorCode(code: unknown): StepKey | null {
  if (code === 'query_failed' || code === 'graph_invalid') return 'query';
  if (code === 'render_failed') return 'slack';
  return null;
}

export function useDryRun({ onStaleCleared }: DryRunOptions): UseDryRunReturn {
  const orpc = useOrpc();
  const [result, setResult] = React.useState<TestRunResult | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [stepErrors, setStepErrors] = React.useState<StepErrorMap>({});
  const [columns, setColumns] = React.useState<string[]>([]);

  // Monotonically increasing counter — each call to run() increments this.
  // The current value is captured in a closure at call time and compared in
  // onSuccess/onError so that a slow, older in-flight response never overwrites
  // state that has already been updated by a newer request.
  const latestSeq = React.useRef(0);

  const mutation = useMutation(orpc.workflow.dryRunDraft.mutationOptions());

  function run(draft: WorkflowDraft): void {
    setError(null);
    setStepErrors({});
    // Bump the sequence counter and capture the current value for this call.
    // The seq is closed over by the per-call onSuccess/onError callbacks below,
    // ensuring that a slow, older in-flight response is silently dropped if a
    // newer request has already been issued.
    const seq = ++latestSeq.current;
    const graph = draftToGraph(draft);
    // warehouseId is intentionally NOT sent — the backend derives it from the graph's
    // query node (graph.nodes.find(n => n.kind === 'query').config.warehouseId), exactly
    // as dryRun({id}) does. Sending a separate warehouseId could create a mismatch
    // between the preview warehouse and the live-run warehouse.
    //
    // trigger IS sent so the backend can compute the REAL cadence window for
    // {{range_start}}/{{range_end}} queries (e.g. ~24h for a daily schedule rather than
    // the 1h fallback that fires when no trigger is available). This ensures preview
    // window == scheduled-run window.
    mutation.mutate(
      {
        graph: graph,
        trigger: scheduleToTrigger(draft.schedule),
      },
      {
        onSuccess: (data) => {
          // Stale-response guard: drop the result if a newer request has been issued.
          if (seq < latestSeq.current) return;

          const r = data.result;

          // FIX 2: validationErrors → treat as error state, NOT a rendered preview.
          // Clear any stale success result so the old "Preview — not sent" bubble is gone.
          // Do NOT clear stale-step markers; do NOT populate result with the new error.
          if (r.slack.validationErrors && r.slack.validationErrors.length > 0) {
            setResult(null);
            const joined = r.slack.validationErrors.join('; ');
            setError(joined);
            // Template render errors are a Slack-node problem — scope them to the Slack step so the
            // query/condition steps don't show a borrowed error (Issue 3).
            setStepErrors({ slack: joined });
            // Still update columns from the query result (the query succeeded).
            setColumns(r.query.sampleColumns);
            return;
          }

          // Map backend DryRunResult to frontend TestRunResult.
          // The shapes are intentionally identical (see LLD §2.1).
          const mapped: TestRunResult = {
            query: {
              sampleColumns: r.query.sampleColumns,
              sampleRows: r.query.sampleRows,
              rowCount: r.query.rowCount,
            },
            condition: r.condition ?? null,
            slack: {
              renderedMessage: r.slack.renderedMessage ?? '',
            },
          };

          setResult(mapped);
          setError(null);
          setStepErrors({});
          setColumns(r.query.sampleColumns);

          // Clear stale on all steps that the dry-run covered.
          const cleared = new Set<StepKey>(['query', 'slack']);
          if (r.condition !== null) cleared.add('condition');
          onStaleCleared(cleared);
        },
        onError: (err) => {
          // Stale-response guard: drop the error if a newer request has been issued.
          if (seq < latestSeq.current) return;

          // Input-validation (Zod) errors carry precise per-issue paths — map each to the node it
          // concerns so every invalid step shows ITS OWN reason and valid steps show nothing (Issue 3).
          const issues = extractIssues(err);
          if (issues) {
            const perStep = issuesToStepErrors(issues, graph.nodes);
            setStepErrors(perStep);
            const flagged = Object.keys(perStep).length;
            // Banner is a count summary, not the per-node text (which lives on the steps).
            setError(
              flagged <= 1
                ? 'Fix the error in the highlighted step below.'
                : `Fix the errors across the ${flagged} highlighted steps below.`,
            );
            return;
          }

          // No structured issues — a single handler error (bad SQL, warehouse timeout, render error…).
          // Surface the real message in the banner, and scope it to the relevant step when the
          // DryRunError code tells us which one (query_failed/graph_invalid→query, render_failed→slack).
          const msg = err instanceof Error ? err.message : 'Dry run failed';
          setError(msg);
          const code = (err as { data?: { code?: unknown } }).data?.code;
          const step = stepForErrorCode(code);
          setStepErrors(step ? { [step]: msg } : {});
          // Keep prior fresh results — only the step that failed should show the error.
        },
      },
    );
  }

  function clear(): void {
    setResult(null);
    setError(null);
    setStepErrors({});
    setColumns([]);
  }

  return {
    run,
    result,
    isRunning: mutation.isPending,
    error,
    stepErrors,
    columns,
    clear,
  };
}

import React from 'react';
